import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Radio,
  Checkbox,
  DatePicker,
  Upload,
  Rate,
  Button,
  Space,
  Typography,
  Steps,
  Divider,
  Spin,
  Alert,
  Table,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SendOutlined,
  UploadOutlined,
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
} from '@ant-design/icons';
import jsonLogic from 'json-logic-js';
import LibraryPage from '../../../components/layout/LibraryPage';
import { useGetTemplateQuery, useSubmitResponseMutation } from '../../../store/api/templateApi';
import { toast } from '../../../components/common/Toast';
import RichTextEditor from '../../../components/common/RichTextEditor';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Table/Grid field component
const TableField = ({ question, form, fieldProps }) => {
  const tableConfig = question.tableConfig || { columns: [], allowAdd: true, allowDelete: true, editMode: 'inline', orientation: 'columns' };
  const [dataSource, setDataSource] = useState([]);
  const [editingKey, setEditingKey] = useState('');
  const [rowData, setRowData] = useState({});

  const isRowBased = tableConfig.orientation === 'rows';

  // Initialize with form value or empty row/data
  useEffect(() => {
    const savedData = form.getFieldValue(question.id);

    if (isRowBased) {
      // For row-based, initialize as object with keys from columns
      if (savedData && typeof savedData === 'object' && !Array.isArray(savedData)) {
        setRowData(savedData);
      } else {
        const initialData = {};
        tableConfig.columns.forEach(col => {
          initialData[col.key] = col.type === 'checkbox' || col.type === 'multiselect' ? [] : '';
        });
        setRowData(initialData);
      }
    } else {
      // For column-based, initialize as array
      if (savedData && Array.isArray(savedData)) {
        setDataSource(savedData);
      } else if (dataSource.length === 0) {
        setDataSource([{ key: Date.now() }]);
      }
    }
  }, []);

  // Update form value when data changes
  useEffect(() => {
    if (isRowBased) {
      form.setFieldValue(question.id, rowData);
    } else {
      form.setFieldValue(question.id, dataSource);
    }
  }, [isRowBased ? rowData : dataSource]);

  const isEditing = (record) => {
    if (tableConfig.editMode === 'inline') return true;
    return record.key === editingKey;
  };

  const handleEdit = (key) => {
    setEditingKey(key);
  };

  const handleSave = () => {
    setEditingKey('');
  };

  const handleAddRow = () => {
    const newKey = Date.now();
    setDataSource([...dataSource, { key: newKey }]);
    if (tableConfig.editMode === 'row') {
      setEditingKey(newKey);
    }
  };

  const handleDeleteRow = (key) => {
    setDataSource(dataSource.filter((row) => row.key !== key));
  };

  const handleCellChange = (key, columnKey, value) => {
    const newData = dataSource.map((row) => {
      if (row.key === key) {
        return { ...row, [columnKey]: value };
      }
      return row;
    });
    setDataSource(newData);
  };

  const handleRowChange = (columnKey, value) => {
    setRowData({ ...rowData, [columnKey]: value });
  };

  const renderCell = (text, record, column) => {
    const editable = isEditing(record);

    if (question.read_only || !editable) {
      // Display-only mode
      if (column.type === 'checkbox') {
        return <Checkbox checked={record[column.key]} disabled />;
      }
      if (Array.isArray(text)) {
        return text.join(', ') || '-';
      }
      return text || '-';
    }

    // Editable mode
    switch (column.type) {
      case 'text':
        return (
          <Input
            value={record[column.key]}
            onChange={(e) => handleCellChange(record.key, column.key, e.target.value)}
            placeholder={`Enter ${column.title}`}
            size="small"
          />
        );

      case 'textarea':
        return (
          <Input.TextArea
            value={record[column.key]}
            onChange={(e) => handleCellChange(record.key, column.key, e.target.value)}
            placeholder={`Enter ${column.title}`}
            size="small"
            rows={2}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        );

      case 'number':
      case 'currency':
      case 'percentage':
        return (
          <InputNumber
            value={record[column.key]}
            onChange={(value) => handleCellChange(record.key, column.key, value)}
            placeholder={`Enter ${column.title}`}
            size="small"
            style={{ width: '100%' }}
            prefix={column.type === 'currency' ? '$' : undefined}
            formatter={column.type === 'percentage' ? (value) => `${value}%` : undefined}
            parser={column.type === 'percentage' ? (value) => value.replace('%', '') : undefined}
          />
        );

      case 'date':
        return (
          <DatePicker
            value={record[column.key]}
            onChange={(date) => handleCellChange(record.key, column.key, date)}
            size="small"
            style={{ width: '100%' }}
          />
        );

      case 'datetime':
        return (
          <DatePicker
            showTime
            value={record[column.key]}
            onChange={(date) => handleCellChange(record.key, column.key, date)}
            size="small"
            style={{ width: '100%' }}
          />
        );

      case 'time':
        return (
          <DatePicker
            picker="time"
            value={record[column.key]}
            onChange={(time) => handleCellChange(record.key, column.key, time)}
            size="small"
            style={{ width: '100%' }}
          />
        );

      case 'daterange':
        return (
          <DatePicker.RangePicker
            value={record[column.key]}
            onChange={(dates) => handleCellChange(record.key, column.key, dates)}
            size="small"
            style={{ width: '100%' }}
          />
        );

      case 'select':
        return (
          <Select
            value={record[column.key]}
            onChange={(value) => handleCellChange(record.key, column.key, value)}
            placeholder={`Select ${column.title}`}
            size="small"
            style={{ width: '100%' }}
            options={(column.options || []).map(opt => ({ label: opt, value: opt }))}
          />
        );

      case 'multiselect':
        return (
          <Select
            mode="multiple"
            value={record[column.key]}
            onChange={(value) => handleCellChange(record.key, column.key, value)}
            placeholder={`Select ${column.title}`}
            size="small"
            style={{ width: '100%' }}
            options={(column.options || []).map(opt => ({ label: opt, value: opt }))}
          />
        );

      case 'radio':
        return (
          <Radio.Group
            value={record[column.key]}
            onChange={(e) => handleCellChange(record.key, column.key, e.target.value)}
            size="small"
          >
            <Space direction="vertical" size="small">
              {(column.options || []).map((opt, idx) => (
                <Radio key={idx} value={opt}>{opt}</Radio>
              ))}
            </Space>
          </Radio.Group>
        );

      case 'checkbox':
        return (
          <Checkbox
            checked={record[column.key]}
            onChange={(e) => handleCellChange(record.key, column.key, e.target.checked)}
          />
        );

      default:
        return text || '-';
    }
  };

  const columns = [
    ...tableConfig.columns.map((col) => ({
      title: col.title,
      dataIndex: col.key,
      key: col.key,
      width: col.width || 150,
      render: (text, record) => renderCell(text, record, col),
    })),
    ...((tableConfig.allowDelete || tableConfig.editMode === 'row') && !question.read_only
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: tableConfig.editMode === 'row' ? 150 : 80,
            render: (_, record) => {
              const editable = isEditing(record);
              return (
                <Space size="small">
                  {tableConfig.editMode === 'row' && (
                    editable ? (
                      <Button
                        icon={<SaveOutlined />}
                        size="small"
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEdit(record.key)}
                        disabled={editingKey !== ''}
                      >
                        Edit
                      </Button>
                    )
                  )}
                  {tableConfig.allowDelete && (
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => handleDeleteRow(record.key)}
                      disabled={dataSource.length === 1 || (tableConfig.editMode === 'row' && editingKey !== '' && editingKey !== record.key)}
                    />
                  )}
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  // Render input for a row field (row-based orientation)
  const renderRowField = (column) => {
    const value = rowData[column.key];

    if (question.read_only) {
      // Display-only mode
      if (column.type === 'checkbox') {
        return <Checkbox checked={value} disabled />;
      }
      if (Array.isArray(value)) {
        return <Text>{value.join(', ') || '—'}</Text>;
      }
      return <Text>{value || '—'}</Text>;
    }

    // Editable mode - reuse the same field logic from renderCell
    switch (column.type) {
      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handleRowChange(column.key, e.target.value)}
            placeholder={`Enter ${column.title}`}
          />
        );

      case 'textarea':
        return (
          <Input.TextArea
            value={value}
            onChange={(e) => handleRowChange(column.key, e.target.value)}
            placeholder={`Enter ${column.title}`}
            rows={3}
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
        );

      case 'number':
      case 'currency':
      case 'percentage':
        return (
          <InputNumber
            value={value}
            onChange={(val) => handleRowChange(column.key, val)}
            placeholder={`Enter ${column.title}`}
            style={{ width: '100%' }}
            prefix={column.type === 'currency' ? '$' : undefined}
            formatter={column.type === 'percentage' ? (val) => `${val}%` : undefined}
            parser={column.type === 'percentage' ? (val) => val.replace('%', '') : undefined}
          />
        );

      case 'date':
        return (
          <DatePicker
            value={value}
            onChange={(date) => handleRowChange(column.key, date)}
            style={{ width: '100%' }}
          />
        );

      case 'datetime':
        return (
          <DatePicker
            showTime
            value={value}
            onChange={(date) => handleRowChange(column.key, date)}
            style={{ width: '100%' }}
          />
        );

      case 'time':
        return (
          <DatePicker
            picker="time"
            value={value}
            onChange={(time) => handleRowChange(column.key, time)}
            style={{ width: '100%' }}
          />
        );

      case 'select':
        return (
          <Select
            value={value}
            onChange={(val) => handleRowChange(column.key, val)}
            placeholder={`Select ${column.title}`}
            style={{ width: '100%' }}
            options={(column.options || []).map(opt => ({ label: opt, value: opt }))}
          />
        );

      case 'multiselect':
        return (
          <Select
            mode="multiple"
            value={value}
            onChange={(val) => handleRowChange(column.key, val)}
            placeholder={`Select ${column.title}`}
            style={{ width: '100%' }}
            options={(column.options || []).map(opt => ({ label: opt, value: opt }))}
          />
        );

      case 'radio':
        return (
          <Radio.Group
            value={value}
            onChange={(e) => handleRowChange(column.key, e.target.value)}
          >
            <Space direction="vertical">
              {(column.options || []).map((opt, idx) => (
                <Radio key={idx} value={opt}>{opt}</Radio>
              ))}
            </Space>
          </Radio.Group>
        );

      case 'checkbox':
        return (
          <Checkbox
            checked={value}
            onChange={(e) => handleRowChange(column.key, e.target.checked)}
          />
        );

      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleRowChange(column.key, e.target.value)}
            placeholder={`Enter ${column.title}`}
          />
        );
    }
  };

  if (tableConfig.columns.length === 0) {
    return (
      <Form.Item {...fieldProps}>
        <Alert
          message="Table Not Configured"
          description={`This table has no ${isRowBased ? 'rows' : 'columns'} defined. Please configure the table in the template builder.`}
          type="warning"
          showIcon
        />
      </Form.Item>
    );
  }

  // Row-based orientation rendering
  if (isRowBased) {
    return (
      <Form.Item {...fieldProps}>
        <div style={{ border: '1px solid #E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
          {tableConfig.columns.map((column, idx) => (
            <div
              key={column.key}
              style={{
                display: 'flex',
                borderBottom: idx < tableConfig.columns.length - 1 ? '1px solid #E5E7EB' : 'none',
                minHeight: '40px',
              }}
            >
              <div
                style={{
                  width: '200px',
                  padding: '12px',
                  background: '#FAFAFA',
                  borderRight: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Text strong style={{ fontSize: '13px' }}>
                  {column.title}
                  {column.required && <Text type="danger"> *</Text>}
                </Text>
              </div>
              <div style={{ flex: 1, padding: '8px 12px', display: 'flex', alignItems: 'center' }}>
                {renderRowField(column)}
              </div>
            </div>
          ))}
        </div>
      </Form.Item>
    );
  }

  // Column-based orientation rendering (original table)
  return (
    <Form.Item {...fieldProps}>
      <div>
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          size="small"
          bordered
          style={{ marginBottom: 8 }}
        />
        {tableConfig.allowAdd && !question.read_only && (
          <Button
            icon={<PlusOutlined />}
            onClick={handleAddRow}
            size="small"
            disabled={tableConfig.editMode === 'row' && editingKey !== ''}
          >
            Add Row
          </Button>
        )}
      </div>
    </Form.Item>
  );
};

// Repeater field component - allows repeating a section multiple times
const RepeaterField = ({ question, form, fieldProps }) => {
  const [items, setItems] = useState([]);
  const repeaterConfig = question.repeaterConfig || {};
  const hasCustomFields = repeaterConfig.fields && repeaterConfig.fields.length > 0;
  const itemLabel = repeaterConfig.itemLabel || 'Item';

  // Initialize with form value or empty item
  useEffect(() => {
    const savedData = form.getFieldValue(question.id);
    if (savedData && Array.isArray(savedData)) {
      setItems(savedData);
    } else if (items.length === 0) {
      const initialItem = { key: Date.now() };
      if (hasCustomFields) {
        // Initialize with empty values for all custom fields
        repeaterConfig.fields.forEach(field => {
          initialItem[field.key] = field.type === 'checkbox' || field.type === 'multiselect' ? [] : '';
        });
      } else {
        // Simple mode - just data field
        initialItem.data = '';
      }
      setItems([initialItem]);
    }
  }, []);

  // Update form value when items change
  useEffect(() => {
    form.setFieldValue(question.id, items);
  }, [items]);

  const handleAddItem = () => {
    const newItem = { key: Date.now() };
    if (hasCustomFields) {
      repeaterConfig.fields.forEach(field => {
        newItem[field.key] = field.type === 'checkbox' || field.type === 'multiselect' ? [] : '';
      });
    } else {
      newItem.data = '';
    }
    setItems([...items, newItem]);
  };

  const handleDeleteItem = (key) => {
    setItems(items.filter((item) => item.key !== key));
  };

  const handleItemChange = (key, fieldKey, value) => {
    const newItems = items.map((item) => {
      if (item.key === key) {
        return { ...item, [fieldKey]: value };
      }
      return item;
    });
    setItems(newItems);
  };

  // Render field based on type
  const renderField = (field, item) => {
    const value = item[field.key];
    const onChange = (val) => handleItemChange(item.key, field.key, val);

    switch (field.type) {
      case 'text':
        return <Input placeholder={field.placeholder} value={value} onChange={(e) => onChange(e.target.value)} />;

      case 'textarea':
        return <TextArea rows={3} placeholder={field.placeholder} value={value} onChange={(e) => onChange(e.target.value)} />;

      case 'richtext':
        return (
          <RichTextEditor
            value={value || ''}
            onChange={onChange}
            placeholder={field.placeholder}
          />
        );

      case 'number':
      case 'currency':
      case 'percentage':
        return (
          <InputNumber
            style={{ width: '100%' }}
            placeholder={field.placeholder}
            value={value}
            onChange={onChange}
            prefix={field.type === 'currency' ? '$' : undefined}
            formatter={field.type === 'percentage' ? (val) => `${val}%` : undefined}
            parser={field.type === 'percentage' ? (val) => val.replace('%', '') : undefined}
          />
        );

      case 'email':
        return <Input type="email" placeholder={field.placeholder} value={value} onChange={(e) => onChange(e.target.value)} />;

      case 'phone':
        return <Input placeholder={field.placeholder} value={value} onChange={(e) => onChange(e.target.value)} />;

      case 'url':
        return <Input placeholder={field.placeholder} value={value} onChange={(e) => onChange(e.target.value)} />;

      case 'date':
        return <DatePicker style={{ width: '100%' }} value={value} onChange={onChange} />;

      case 'datetime':
        return <DatePicker showTime style={{ width: '100%' }} value={value} onChange={onChange} />;

      case 'time':
        return <DatePicker.TimePicker style={{ width: '100%' }} value={value} onChange={onChange} />;

      case 'select':
        return (
          <Select
            placeholder={field.placeholder}
            value={value}
            onChange={onChange}
            options={(field.options || []).map(opt => ({ label: opt, value: opt }))}
          />
        );

      case 'multiselect':
        return (
          <Select
            mode="multiple"
            placeholder={field.placeholder}
            value={value}
            onChange={onChange}
            options={(field.options || []).map(opt => ({ label: opt, value: opt }))}
          />
        );

      case 'radio':
        return (
          <Radio.Group value={value} onChange={(e) => onChange(e.target.value)}>
            <Space direction="vertical">
              {(field.options || []).map((opt, idx) => (
                <Radio key={idx} value={opt}>{opt}</Radio>
              ))}
            </Space>
          </Radio.Group>
        );

      case 'checkbox':
        return (
          <Checkbox.Group value={value} onChange={onChange}>
            <Space direction="vertical">
              {(field.options || []).map((opt, idx) => (
                <Checkbox key={idx} value={opt}>{opt}</Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        );

      case 'file':
        return (
          <Upload>
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        );

      default:
        return <Input placeholder={field.placeholder} value={value} onChange={(e) => onChange(e.target.value)} />;
    }
  };

  return (
    <Form.Item {...fieldProps}>
      <div>
        {items.map((item, index) => (
          <Card
            key={item.key}
            size="small"
            title={`${itemLabel} ${index + 1}`}
            style={{ marginBottom: 12 }}
            extra={
              !question.read_only && items.length > (repeaterConfig.minItems || 1) ? (
                <Button
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  onClick={() => handleDeleteItem(item.key)}
                />
              ) : null
            }
          >
            {hasCustomFields ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {repeaterConfig.fields.map((field, idx) => (
                  <div key={idx}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                      {field.label} {field.required && <Text type="danger">*</Text>}
                    </Text>
                    {renderField(field, item)}
                  </div>
                ))}
              </Space>
            ) : (
              <Input.TextArea
                value={item.data}
                onChange={(e) => handleItemChange(item.key, 'data', e.target.value)}
                placeholder="Enter details"
                rows={3}
                disabled={question.read_only}
              />
            )}
          </Card>
        ))}
        {!question.read_only && items.length < (repeaterConfig.maxItems || 50) && (
          <Button icon={<PlusOutlined />} onClick={handleAddItem} size="small" style={{ marginTop: 8 }}>
            Add {itemLabel}
          </Button>
        )}
      </div>
    </Form.Item>
  );
};

// Number field wrapper that shows error for invalid input
const NumberField = ({ value, onChange, placeholder, disabled, onBlur, style, prefix, precision, min, max, formatter, parser }) => {
  const [hasError, setHasError] = useState(false);
  const [lastValue, setLastValue] = useState(value);

  return (
    <div>
      <InputNumber
        style={style}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        prefix={prefix}
        precision={precision}
        min={min}
        max={max}
        formatter={formatter}
        parser={parser}
        onChange={(val) => {
          setLastValue(val);
          if (val === null || val === undefined) {
            // Invalid input - InputNumber cleared it
            setHasError(true);
          } else {
            setHasError(false);
          }
          onChange?.(val);
        }}
        onBlur={(e) => {
          const rawValue = e.target.value;
          if (rawValue && rawValue.trim() !== '' && (lastValue === null || lastValue === undefined)) {
            setHasError(true);
          }
          onBlur?.(e);
        }}
        status={hasError ? 'error' : ''}
      />
      {hasError && (
        <div style={{ color: '#ff4d4f', fontSize: '14px', marginTop: '4px' }}>
          Please enter a valid number (digits only)
        </div>
      )}
    </div>
  );
};

// Helper to check if question should be visible based on conditional logic
const shouldShowQuestion = (question, formValues) => {
  if (!question.conditionalLogic) return true;


  try {
    const result = jsonLogic.apply(question.conditionalLogic, formValues);
    return !!result;
  } catch (error) {
    console.error('Error evaluating conditional logic:', error, question.conditionalLogic);
    return true; // Show field by default if evaluation fails
  }
};

// Component to render questions with conditional logic
const ConditionalQuestions = ({ questions, form, renderField }) => {
  // Watch all form values to trigger re-render when they change
  const formValues = Form.useWatch([], form) || {};

  return (
    <>
      {questions?.map((question, qIdx) => {
        const isVisible = shouldShowQuestion(question, formValues);
        if (!isVisible) return null;

        return <div key={qIdx}>{renderField(question)}</div>;
      })}
    </>
  );
};

export default function TemplateComplete() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [activeSheet, setActiveSheet] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);

  const { data: template, isLoading, error } = useGetTemplateQuery(id);
  const [submitResponse] = useSubmitResponseMutation();

  // Check if all required fields are filled
  const checkFormValidity = async () => {
    try {
      await form.validateFields();
      setCanSubmit(true);
    } catch (error) {
      setCanSubmit(false);
    }
  };

  // Validate on form value changes
  const handleValuesChange = () => {
    // Debounce validation check
    const timer = setTimeout(() => {
      checkFormValidity();
    }, 300);
    return () => clearTimeout(timer);
  };

  const handleBackToList = () => {
    navigate('/library/published');
  };

  const handleSaveDraft = async () => {
    try {
      const values = form.getFieldsValue();

      // Calculate completion percentage
      const allFields = Object.keys(values);
      const filledFields = allFields.filter(key => values[key] !== undefined && values[key] !== null && values[key] !== '');
      const completion_percentage = allFields.length > 0 ? (filledFields.length / allFields.length) * 100 : 0;

      await submitResponse({
        templateId: id,
        response_data: values,
        status: 'draft',
        completion_percentage,
      }).unwrap();

      toast.success('Draft saved successfully');
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error(error?.data?.error || 'Failed to save draft');
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();

      console.log('Form values before submit:', values);

      // Calculate completion percentage
      const allFields = Object.keys(values);
      const filledFields = allFields.filter(key => values[key] !== undefined && values[key] !== null && values[key] !== '');
      const completion_percentage = allFields.length > 0 ? (filledFields.length / allFields.length) * 100 : 0;

      console.log('Submitting response:', {
        templateId: id,
        response_data: values,
        status: 'submitted',
        completion_percentage,
      });

      const result = await submitResponse({
        templateId: id,
        response_data: values,
        status: 'submitted',
        completion_percentage,
      }).unwrap();

      console.log('Submit result:', result);
      toast.success('Response submitted successfully');
      navigate('/library');
    } catch (error) {
      console.error('Failed to submit response:', error);
      if (error.errorFields) {
        toast.error('Please fill in all required fields');
        // Scroll to first error
        form.scrollToField(error.errorFields[0].name);
      } else {
        toast.error(error?.data?.error || 'Failed to submit response');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (question) => {
    const fieldProps = {
      name: question.id,
      label: question.question,
      rules: question.required ? [{ required: true, message: 'This field is required' }] : [],
      help: question.help_text,
      validateTrigger: ['onChange', 'onBlur'],
    };

    switch (question.field_type) {
      case 'text':
        return (
          <Form.Item {...fieldProps}>
            <Input placeholder="Enter text" disabled={question.read_only} />
          </Form.Item>
        );

      case 'textarea':
        return (
          <Form.Item {...fieldProps}>
            <TextArea rows={4} placeholder="Enter text" disabled={question.read_only} />
          </Form.Item>
        );

      case 'richtext':
        return (
          <Form.Item {...fieldProps}>
            <RichTextEditor
              value={form.getFieldValue(question.id) || ''}
              onChange={(html) => form.setFieldValue(question.id, html)}
              placeholder={question.help_text || 'Enter formatted text...'}
              disabled={question.read_only}
            />
          </Form.Item>
        );

      case 'number':
        return (
          <Form.Item {...fieldProps}>
            <NumberField
              style={{ width: '100%' }}
              placeholder="Enter number"
              disabled={question.read_only}
            />
          </Form.Item>
        );

      case 'email':
        return (
          <Form.Item {...fieldProps} rules={[...fieldProps.rules, { type: 'email', message: 'Invalid email' }]}>
            <Input type="email" placeholder="Enter email" disabled={question.read_only} />
          </Form.Item>
        );

      case 'phone':
        return (
          <Form.Item
            {...fieldProps}
            rules={[
              ...fieldProps.rules,
              {
                pattern: /^[\d\s\-+()]+$/,
                message: 'Please enter a valid phone number',
              },
            ]}
          >
            <Input placeholder="Enter phone number" disabled={question.read_only} />
          </Form.Item>
        );

      case 'url':
        return (
          <Form.Item {...fieldProps} rules={[...fieldProps.rules, { type: 'url', message: 'Invalid URL' }]}>
            <Input placeholder="https://" disabled={question.read_only} />
          </Form.Item>
        );

      case 'select':
        return (
          <Form.Item {...fieldProps}>
            <Select
              placeholder="Select an option"
              options={question.options?.map((opt) => ({
                label: opt.label || opt,
                value: opt.label || opt.value || opt,
              }))}
              disabled={question.read_only}
            />
          </Form.Item>
        );

      case 'multiselect':
        return (
          <Form.Item {...fieldProps}>
            <Select
              mode="multiple"
              placeholder="Select options"
              options={question.options?.map((opt) => ({
                label: opt.label || opt,
                value: opt.label || opt.value || opt,
              }))}
              disabled={question.read_only}
            />
          </Form.Item>
        );

      case 'radio':
        return (
          <Form.Item {...fieldProps}>
            <Radio.Group disabled={question.read_only}>
              <Space direction="vertical">
                {question.options?.map((opt, idx) => (
                  <Radio key={idx} value={opt.label || opt.value || opt}>
                    {opt.label || opt}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
        );

      case 'checkbox':
        return (
          <Form.Item {...fieldProps} valuePropName="checked">
            <Checkbox.Group disabled={question.read_only}>
              <Space direction="vertical">
                {question.options?.map((opt, idx) => (
                  <Checkbox key={idx} value={opt.label || opt.value || opt}>
                    {opt.label || opt}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item {...fieldProps}>
            <DatePicker style={{ width: '100%' }} disabled={question.read_only} />
          </Form.Item>
        );

      case 'datetime':
        return (
          <Form.Item {...fieldProps}>
            <DatePicker showTime style={{ width: '100%' }} disabled={question.read_only} />
          </Form.Item>
        );

      case 'file':
        return (
          <Form.Item {...fieldProps} valuePropName="fileList" getValueFromEvent={(e) => e.fileList}>
            <Upload beforeUpload={() => false} disabled={question.read_only}>
              <Button icon={<UploadOutlined />}>Upload File</Button>
            </Upload>
          </Form.Item>
        );

      case 'rating':
        return (
          <Form.Item {...fieldProps}>
            <Rate disabled={question.read_only} />
          </Form.Item>
        );

      case 'currency':
        return (
          <Form.Item {...fieldProps}>
            <NumberField
              style={{ width: '100%' }}
              prefix="$"
              precision={2}
              placeholder="0.00"
              disabled={question.read_only}
              min={0}
            />
          </Form.Item>
        );

      case 'percentage':
        return (
          <Form.Item {...fieldProps}>
            <NumberField
              style={{ width: '100%' }}
              min={0}
              max={100}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace('%', '')}
              disabled={question.read_only}
            />
          </Form.Item>
        );

      case 'table':
        return <TableField question={question} form={form} fieldProps={fieldProps} />;

      case 'repeater':
        return <RepeaterField question={question} form={form} fieldProps={fieldProps} />;

      default:
        return (
          <Form.Item {...fieldProps}>
            <Input placeholder="Enter value" disabled={question.read_only} />
          </Form.Item>
        );
    }
  };

  if (isLoading) {
    return (
      <LibraryPage title="Loading...">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Spin size="large" />
        </div>
      </LibraryPage>
    );
  }

  if (error || !template) {
    return (
      <LibraryPage title="Error">
        <Alert
          message="Failed to load template"
          description="The template could not be loaded. Please try again."
          type="error"
          showIcon
        />
        <Button
          style={{ marginTop: 16 }}
          onClick={() => navigate('/library/published')}
          icon={<ArrowLeftOutlined />}
        >
          Back to Published Templates
        </Button>
      </LibraryPage>
    );
  }

  const sheets = template.template_data?.sheets || [];
  const totalSteps = sheets.length + 1; // Add 1 for Review & Submit step
  const isReviewStep = activeSheet === sheets.length;

  return (
    <LibraryPage
      title={template.name}
      breadcrumbs={[
        { label: 'Library', path: '/library' },
        { label: 'Published', path: '/library/published' },
        { label: template.name },
      ]}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Stepper for multi-sheet navigation */}
            <Steps
              current={activeSheet}
              onChange={(current) => setActiveSheet(current)}
              style={{ marginBottom: 24 }}
              items={[
                ...sheets.map((sheet, idx) => ({
                  title: sheet.name,
                  status: activeSheet === idx ? 'process' : activeSheet > idx ? 'finish' : 'wait',
                })),
                {
                  title: 'Review & Submit',
                  status: isReviewStep ? 'process' : activeSheet > sheets.length ? 'finish' : 'wait',
                },
              ]}
            />

            {/* Form - Render all sheets to preserve values, but only display active sheet */}
            <Form form={form} layout="vertical" size="small" onValuesChange={handleValuesChange}>
              {sheets.map((sheet, sheetIdx) => (
                <div key={sheetIdx} style={{ display: !isReviewStep && sheetIdx === activeSheet ? 'block' : 'none' }}>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {sheet.sections?.map((section, sectionIdx) => (
                      <Card
                        key={sectionIdx}
                        title={section.name}
                        type="inner"
                        style={{ background: '#F9FAFB' }}
                      >
                        {section.categories?.map((category, catIdx) => (
                          <div
                            key={catIdx}
                            style={{
                              marginBottom: 16,
                              paddingBottom: catIdx < (section.categories?.length || 0) - 1 ? 16 : 0,
                              borderBottom: catIdx < (section.categories?.length || 0) - 1 ? '1px solid #E5E7EB' : 'none'
                            }}
                          >
                            {category.name && (
                              <Title level={5} style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600 }}>
                                {category.name}
                              </Title>
                            )}
                            <ConditionalQuestions
                              questions={category.questions}
                              form={form}
                              renderField={renderField}
                            />
                          </div>
                        ))}
                      </Card>
                    ))}
                  </Space>
                </div>
              ))}

              {/* Review & Submit Screen */}
              {isReviewStep && (
                <div>
                  <Alert
                    message="Review Your Responses"
                    description="Please review all your responses below. If you need to make changes, use the Previous button or click on a step above to navigate back."
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                  />

                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {sheets.map((sheet, sheetIdx) => (
                      <Card
                        key={sheetIdx}
                        title={sheets.length > 1 ? sheet.name : null}
                        style={{ background: '#FAFAFA' }}
                      >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                          {sheet.sections?.map((section, sectionIdx) => (
                            <Card
                              key={sectionIdx}
                              title={section.name}
                              type="inner"
                              size="small"
                              style={{ background: '#fff' }}
                            >
                              {section.categories?.map((category, catIdx) => (
                                <div
                                  key={catIdx}
                                  style={{
                                    marginBottom: catIdx < (section.categories?.length || 0) - 1 ? 16 : 0,
                                    paddingBottom: catIdx < (section.categories?.length || 0) - 1 ? 16 : 0,
                                    borderBottom: catIdx < (section.categories?.length || 0) - 1 ? '1px solid #E5E7EB' : 'none'
                                  }}
                                >
                                  {category.name && (
                                    <Title level={5} style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600 }}>
                                      {category.name}
                                    </Title>
                                  )}
                                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    {category.questions?.map((question, qIdx) => {
                                      const value = form.getFieldValue(question.id);
                                      const isVisible = shouldShowQuestion(question, form.getFieldsValue());

                                      if (!isVisible) return null;

                                      // Handle table/grid fields - render as actual table
                                      if (question.field_type === 'table') {
                                        const tableConfig = question.tableConfig || {};

                                        return (
                                          <div key={qIdx} style={{ marginBottom: 16 }}>
                                            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
                                              {question.question}
                                              {question.required && <Text type="danger"> *</Text>}
                                            </Text>

                                            {tableConfig.orientation === 'rows' ? (
                                              // Row-based table: 2-column layout
                                              <div style={{ border: '1px solid #E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
                                                {tableConfig.columns?.map((column, colIdx) => {
                                                  const val = value?.[column.key];
                                                  let displayVal;

                                                  if (Array.isArray(val)) {
                                                    displayVal = val.join(', ');
                                                  } else if (val && typeof val === 'object' && val.format) {
                                                    displayVal = val.format('YYYY-MM-DD HH:mm');
                                                  } else if (val instanceof Date) {
                                                    displayVal = val.toLocaleDateString();
                                                  } else if (val) {
                                                    displayVal = String(val);
                                                  } else {
                                                    displayVal = '—';
                                                  }

                                                  return (
                                                    <div
                                                      key={colIdx}
                                                      style={{
                                                        display: 'flex',
                                                        borderBottom: colIdx < tableConfig.columns.length - 1 ? '1px solid #E5E7EB' : 'none',
                                                      }}
                                                    >
                                                      <div style={{
                                                        width: '200px',
                                                        padding: '8px 12px',
                                                        background: '#FAFAFA',
                                                        borderRight: '1px solid #E5E7EB',
                                                      }}>
                                                        <Text strong style={{ fontSize: '13px' }}>{column.title}</Text>
                                                      </div>
                                                      <div style={{ flex: 1, padding: '8px 12px' }}>
                                                        <Text style={{ fontSize: '13px', color: '#595959' }}>{displayVal}</Text>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              // Column-based table: traditional table
                                              <Table
                                                dataSource={Array.isArray(value) ? value : []}
                                                columns={tableConfig.columns?.map(col => ({
                                                  title: col.title,
                                                  dataIndex: col.key,
                                                  key: col.key,
                                                  render: (text) => {
                                                    if (Array.isArray(text)) return text.join(', ');
                                                    if (!text) return '—';
                                                    // Format Date objects
                                                    if (text && typeof text === 'object' && text.format) {
                                                      return text.format('YYYY-MM-DD HH:mm');
                                                    }
                                                    if (text instanceof Date) {
                                                      return text.toLocaleDateString();
                                                    }
                                                    return String(text);
                                                  }
                                                })) || []}
                                                pagination={false}
                                                size="small"
                                                bordered
                                                locale={{ emptyText: 'No data' }}
                                              />
                                            )}
                                          </div>
                                        );
                                      }

                                      // Format value for display (non-table fields)
                                      let displayValue = value;

                                      // Handle regular arrays (multiselect, checkbox, etc)
                                      if (Array.isArray(value)) {
                                        displayValue = value.join(', ');
                                      } else if (value && typeof value === 'object' && value.format) {
                                        displayValue = value.format('YYYY-MM-DD HH:mm');
                                      } else if (value instanceof Date) {
                                        displayValue = value.toLocaleDateString();
                                      } else if (typeof value === 'boolean') {
                                        displayValue = value ? 'Yes' : 'No';
                                      } else if (!value) {
                                        displayValue = '—';
                                      }

                                      return (
                                        <div key={qIdx} style={{ marginBottom: 12 }}>
                                          <Text strong style={{ display: 'block', marginBottom: 4, fontSize: '13px' }}>
                                            {question.question}
                                            {question.required && <Text type="danger"> *</Text>}
                                          </Text>
                                          <Text style={{ fontSize: '13px', color: '#595959' }}>{displayValue}</Text>
                                        </div>
                                      );
                                    })}
                                  </Space>
                                </div>
                              ))}
                            </Card>
                          ))}
                        </Space>
                      </Card>
                    ))}
                  </Space>
                </div>
              )}
            </Form>

            <Divider />

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Button
                onClick={handleBackToList}
                icon={<ArrowLeftOutlined />}
                size="large"
              >
                Back to List
              </Button>

              <Space size="middle">
                {!isReviewStep && (
                  <Button
                    onClick={handleSaveDraft}
                    icon={<SaveOutlined />}
                    size="large"
                  >
                    Save Draft
                  </Button>
                )}

                <Button
                  onClick={() => setActiveSheet(activeSheet - 1)}
                  icon={<LeftOutlined />}
                  size="large"
                  disabled={activeSheet === 0}
                >
                  Previous
                </Button>

                {!isReviewStep ? (
                  <Button
                    onClick={() => setActiveSheet(activeSheet + 1)}
                    icon={<RightOutlined />}
                    size="large"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    icon={<SendOutlined />}
                    size="large"
                    loading={isSubmitting}
                    disabled={!canSubmit}
                    style={{ background: '#8B5CF6', borderColor: '#8B5CF6' }}
                  >
                    Submit
                  </Button>
                )}
              </Space>
            </div>
          </Space>
        </Card>
      </div>
    </LibraryPage>
  );
}
